import { panelHeaderControlClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import { format } from "date-fns";

interface TodayButtonProps {
  onClick: () => void;
  isTodayVisible: boolean;
  className?: string;
  statsigName?: string;
}

const todayDate = () => new Date().getDate();

export const TodayButton = ({
  onClick,
  isTodayVisible,
  className,
  statsigName = "calendar-nav-today",
}: TodayButtonProps) => {
  // The button's only content is the day number, which on its own reads as a
  // bare "23" to a screen reader. This label names the action, and serves as
  // the tooltip and the accessible name alike.
  const label = `Go to ${format(new Date(), "MMMM d")}`;

  const badge = (
    <span
      className={cn(
        // 16px to sit with the 16px glyphs beside it; the date inside drops to
        // 10px to keep two digits within a box that lost 4px. Medium rather than
        // semibold: the numeral is the only glyph in this row drawn as filled
        // type rather than as strokes, and at 600 it carried more ink than the
        // 1px lines either side of it.
        "size-4 inline-flex items-center justify-center rounded text-[10px] font-medium leading-none",
        // 1px is what the lucide glyphs beside this paint — `stroke-1.5` on a
        // 24px viewBox rendered into 16px. This asked for 1.33px, which was the
        // figure for stroke-2 and stopped being true when the app moved to 1.5;
        // it only looked right because the browser was rounding it back down.
        isTodayVisible ? "border border-current" : "bg-primary text-primary-foreground",
      )}
    >
      {todayDate()}
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClick}
          aria-label={label}
          // Naked, like the header's other icon buttons; the ghost hover supplies
          // the only backdrop. `panelHeaderControlClass` is the metric the rest
          // of that cluster uses — at `size-10` this was the one control in the
          // row standing a third taller than its neighbours.
          className={cn(panelHeaderControlClass, "rounded-full shrink-0", className)}
          data-statsig-name={statsigName}
        >
          {badge}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};
